import { useEffect, useState } from 'react';
import { ApiError, api, formatDateId, formatIdr } from '../api/client';
import type { Plan, Usage } from '../api/types';
import { useSession } from '../lib/session';
import { useConfirm } from '../components/confirm';
import { Alert, Button, Card, DownloadIcon, Spinner } from '../components/ui';
import { useT } from '../i18n/locale';
import {
  BILLING_STATUS_LABEL,
  InvoiceStatus,
  SubscriptionStatus,
  UserRole,
} from '../api/enums';

interface Invoice {
  number: string;
  plan_code: string;
  total_idr: number;
  status: InvoiceStatus;
  payment_url: string | null;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
}

export default function BillingScreen() {
  const { user } = useSession();
  const confirm = useConfirm();
  const t = useT();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invoicePage, setInvoicePage] = useState(1);

  const isOwner = user?.role === UserRole.Owner;
  const isTrial = usage?.status === SubscriptionStatus.Trialing;

  useEffect(() => {
    Promise.all([
      api.get<{ plans: Plan[] }>('/plans'),
      api.get<Usage>('/billing/usage').catch(() => null),
      isOwner
        ? api.get<{ invoices: Invoice[] }>('/billing/invoices').catch(() => ({ invoices: [] }))
        : Promise.resolve({ invoices: [] as Invoice[] }),
    ])
      .then(([p, u, i]) => {
        setPlans(p.plans);
        setUsage(u);
        setInvoices(i.invoices);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : t('billing.loadFailed')))
      .finally(() => setLoading(false));
  }, [isOwner, t]);

  async function checkout(planCode: string) {
    setBusy(planCode);
    setError(null);
    try {
      const res = await api.post<{ paymentUrl: string | null; invoiceNumber: string }>(
        '/billing/checkout',
        { planCode },
      );
      if (res.paymentUrl) {
        window.location.href = res.paymentUrl;
      } else {
        // Manual billing: no hosted checkout, so show the invoice reference
        // for the customer to quote on their bank transfer.
        setError(null);
        await confirm({
          title: t('billing.invoiceCreatedTitle'),
          tone: 'info',
          acknowledgeOnly: true,
          body: t('billing.invoiceCreatedBody'),
          details: [{ label: t('billing.invoiceNo'), value: res.invoiceNumber }],
        });
        const refreshed = await api.get<{ invoices: Invoice[] }>('/billing/invoices');
        setInvoices(refreshed.invoices);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('billing.checkoutFailed'));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Spinner label={t('billing.loading')} />;

  // Client-side paging: the list is already capped at 100, so a page is a
  // slice. The page is clamped rather than reset, so a reload that shortens
  // the list cannot strand the view on a page that no longer exists.
  const invoicesPerPage = 5;
  const invoiceTotalPages = Math.max(1, Math.ceil(invoices.length / invoicesPerPage));
  const invoicePageClamped = Math.min(invoicePage, invoiceTotalPages);
  const pageInvoices = invoices.slice(
    (invoicePageClamped - 1) * invoicesPerPage,
    invoicePageClamped * invoicesPerPage,
  );

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 p-4">
      <h2 className="m-0 text-lg font-bold text-white">{t('billing.heading')}</h2>

      {error ? <Alert tone="error">{error}</Alert> : null}
      {!isOwner ? <Alert tone="info">{t('billing.readonlyNote')}</Alert> : null}

      {usage ? (
        <Card className="p-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                {isTrial ? t('billing.trialPeriod') : t('billing.activePlan')}
              </div>
              <div className="text-xl font-black text-slate-900">
                {isTrial ? t('billing.freeTrial') : usage.planName}
              </div>
              {/*
                Two different dates, now that renewing early no longer moves
                the allowance window. "Kuota direset" is when the row count
                starts again; "aktif sampai" is how long the account is paid
                for. Showing only one of them made an early renewal look like
                it had lost the customer three weeks.
              */}
              <div className="text-xs text-slate-500">
                {isTrial ? (
                  <>{t('billing.trialEnds', { date: formatDateId(usage.periodEnd) })}</>
                ) : (
                  <>
                    {t('billing.quotaResets', { date: formatDateId(usage.periodEnd) })}
                    {usage.paidThrough ? (
                      <> · {t('billing.activeUntil', { date: formatDateId(usage.paidThrough) })}</>
                    ) : null}
                  </>
                )}{' '}
                · <StatusLabel status={usage.status} />
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                {t('billing.rowsUsed')}
              </div>
              <div className="text-xl font-black text-slate-900">
                {usage.rowsUsed.toLocaleString('id-ID')}
                <span className="text-sm font-bold text-slate-400">
                  {' '}
                  / {usage.rowsLimit.toLocaleString('id-ID')}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className={`h-full rounded-full transition-all ${
                usage.exhausted ? 'bg-red-500' : usage.warn ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(100, Math.round(usage.fraction * 100))}%` }}
            />
          </div>

          {isTrial ? (
            <p className="mt-3 mb-0 text-xs text-slate-500">
              {t('billing.trialQuotaNote', { rows: usage.rowsLimit.toLocaleString('id-ID') })}
            </p>
          ) : null}

          {usage.exhausted ? (
            <div className="mt-3">
              <Alert tone="error">{t('billing.exhausted')}</Alert>
            </div>
          ) : usage.warn ? (
            <div className="mt-3">
              <Alert tone="warn">
                {t('billing.warnRemaining', {
                  rows: usage.rowsRemaining.toLocaleString('id-ID'),
                })}
              </Alert>
            </div>
          ) : null}
        </Card>
      ) : null}

      <div className="grid gap-4 md:grid-cols-3">
        {plans.map((plan) => {
          // On a trial no plan has been bought yet, so none is marked active.
          const current = !isTrial && usage?.planCode === plan.code;
          // A paid, live subscription — the only state a downgrade can corrupt.
          // A trial has its own small quota and no paid window, so every plan
          // stays open to it.
          const hasPaidPlan =
            usage != null &&
            (usage.status === SubscriptionStatus.Active ||
              usage.status === SubscriptionStatus.PastDue);
          const isLower = hasPaidPlan && usage != null && plan.sortOrder < usage.planSortOrder;
          const isUpgrade = hasPaidPlan && usage != null && plan.sortOrder > usage.planSortOrder;
          return (
            <Card
              key={plan.code}
              className={`flex flex-col p-5 ${current ? 'border-brand-500 ring-2 ring-brand-500/20' : ''}`}
            >
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <h3 className="m-0 text-lg font-black text-slate-900">{plan.name}</h3>
                  {current ? (
                    <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-bold text-brand-600">
                      {t('common.active')}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 mb-3 text-xs text-slate-500 italic">{plan.tagline}</p>

                <div className="text-2xl font-black text-slate-900">
                  {formatIdr(plan.priceIdr)}
                  <span className="text-xs font-bold text-slate-400"> {t('billing.perMonth')}</span>
                </div>
                <div className="text-[11px] text-slate-400">
                  {t('billing.taxLine', {
                    tax: formatIdr(plan.taxIdr),
                    total: formatIdr(plan.totalIdr),
                  })}
                </div>

                <ul className="mt-4 list-none space-y-1.5 p-0 text-xs text-slate-600">
                  <Feature>
                    <b>{plan.monthlyRows.toLocaleString('id-ID')}</b> {t('billing.rowsPerMonth')}
                  </Feature>
                  <Feature>
                    ± <b>{plan.estRowsPerDay.toLocaleString('id-ID')}</b> {t('billing.rowsPerDay')}
                  </Feature>
                  <Feature>
                    {plan.maxDatabases === null ? (
                      <b>{t('billing.dbUnlimited')}</b>
                    ) : (
                      <>
                        <b>{plan.maxDatabases}</b> {t('billing.dbCount')}
                      </>
                    )}
                  </Feature>
                  <Feature>
                    {plan.maxUsers === null ? (
                      <b>{t('billing.usersUnlimited')}</b>
                    ) : (
                      <>
                        <b>{plan.maxUsers}</b> {t('billing.usersCount')}
                      </>
                    )}
                  </Feature>
                </ul>
              </div>

              {!isOwner ? (
                /*
                 * An admin can read this page but not buy from it, because
                 * the server reserves checkout for the owner. Rendering
                 * nothing left a page of prices with no visible way to act
                 * and no reason given, which reads as a bug rather than a
                 * rule — so say whose decision it is.
                 */
                <p className="mt-5 mb-0 rounded-lg bg-slate-100 px-3 py-2 text-center text-xs font-semibold text-slate-500">
                  {t('billing.ownerOnly')}
                </p>
              ) : isLower ? (
                /*
                 * No downgrades: activating a smaller plan would cut the row
                 * limit below what is already used this cycle. The server
                 * refuses it too; this just explains the disabled option.
                 */
                <p className="mt-5 mb-0 rounded-lg bg-slate-100 px-3 py-2 text-center text-xs font-semibold text-slate-500">
                  {t('billing.belowCurrent')}
                </p>
              ) : (
                <>
                  <Button
                    variant={current ? 'secondary' : 'primary'}
                    className="mt-5 w-full"
                    disabled={busy !== null}
                    onClick={() => void checkout(plan.code)}
                  >
                    {busy === plan.code
                      ? t('common.processing')
                      : current
                        ? t('billing.renew')
                        : isUpgrade
                          ? t('billing.upgrade')
                          : t('billing.choosePlan')}
                  </Button>
                  {isUpgrade ? (
                    <p className="mt-2 mb-0 text-center text-[11px] text-slate-400">
                      {t('billing.upgradeHint')}
                    </p>
                  ) : null}
                </>
              )}
            </Card>
          );
        })}
      </div>

      {isOwner && invoices.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="border-b border-slate-200 bg-slate-50 px-5 py-3">
            <h3 className="m-0 text-base font-bold text-slate-800">{t('billing.invoiceHistory')}</h3>
          </div>
          <table className="w-full border-collapse text-left text-xs">
            <thead className="bg-slate-100/60 text-[11px] font-bold text-slate-500 uppercase">
              <tr>
                <th className="px-3 py-2">{t('billing.invoiceNo')}</th>
                <th className="px-3 py-2">{t('billing.colPlan')}</th>
                <th className="px-3 py-2">{t('billing.colPeriod')}</th>
                <th className="px-3 py-2 text-right">{t('billing.colTotal')}</th>
                <th className="px-3 py-2">{t('billing.colStatus')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {pageInvoices.map((inv) => (
                <tr key={inv.number} className="border-b border-slate-100">
                  <td className="px-3 py-2 font-bold text-slate-800">{inv.number}</td>
                  <td className="px-3 py-2 capitalize">{inv.plan_code}</td>
                  <td className="px-3 py-2">
                    {inv.period_start ? `${formatDateId(inv.period_start)} – ${formatDateId(inv.period_end!)}` : '-'}
                  </td>
                  <td className="px-3 py-2 text-right font-bold">{formatIdr(inv.total_idr)}</td>
                  <td className="px-3 py-2">
                    <StatusLabel status={inv.status} />
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {inv.status === InvoiceStatus.Pending && inv.payment_url ? (
                      <a
                        href={inv.payment_url}
                        className="mr-3 font-bold text-brand-500"
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {t('billing.pay')}
                      </a>
                    ) : null}
                    {/*
                      Only a paid invoice has a PDF — it is a receipt, not a
                      payment request. A plain link, not a fetch: the browser's
                      own download handling files it, and the cookie rides along.
                    */}
                    {inv.status === InvoiceStatus.Paid ? (
                      <a
                        href={`/api/billing/invoices/${encodeURIComponent(inv.number)}/pdf`}
                        className="inline-flex items-center justify-center text-slate-500 hover:text-brand-500"
                        title={t('billing.pdfTitle', { number: inv.number })}
                        aria-label={t('billing.downloadPdf')}
                      >
                        <DownloadIcon className="h-4 w-4" />
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {invoiceTotalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-2.5 text-xs">
              <Button
                variant="ghost"
                disabled={invoicePageClamped <= 1}
                onClick={() => setInvoicePage((p) => Math.max(1, p - 1))}
              >
                {t('billing.prev')}
              </Button>
              <span className="font-semibold text-slate-500">
                {t('billing.pageOf', { page: invoicePageClamped, total: invoiceTotalPages })}
              </span>
              <Button
                variant="ghost"
                disabled={invoicePageClamped >= invoiceTotalPages}
                onClick={() => setInvoicePage((p) => Math.min(invoiceTotalPages, p + 1))}
              >
                {t('billing.next')}
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}

function Feature({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <span className="text-emerald-500">✓</span>
      <span>{children}</span>
    </li>
  );
}

function StatusLabel({ status }: { status: string }) {
  // Status arrives as JSON, so a value outside the union is possible if the
  // server ever adds one; fall back rather than render nothing.
  const t = useT();
  const s = BILLING_STATUS_LABEL[status as keyof typeof BILLING_STATUS_LABEL];
  // A status outside the union would mean the server added one; show the raw
  // value rather than nothing, so it is at least diagnosable.
  if (!s) return <span className="font-bold text-slate-500">{status}</span>;
  return <span className={`font-bold ${s.cls}`}>{t(s.key)}</span>;
}
